import { useRoute } from '@react-navigation/native';
import React, { useEffect, useMemo, useState } from 'react';

import { FormField, Select, Text } from '~/components/core';
import { FONT_SIZE_M } from '~/constants';
import { isDesktop, scaleSize } from '~/constants/scale';
import { useAppSelector, useStyles } from '~/hooks';
import { linkTo } from '~/navigation';
import paths from '~/navigation/paths';
import { Theme } from '~/theme';
import { usePaymentProvider1Methods } from '~/hooks/usePaymentProvider1Methods';
import { accountBalanceSelector } from '~/state/selectors';
import { PaymentProvider1MethodItem } from '~/api-services/OrderConvertService/types';
import { StyleSheet } from 'react-native';
import FlowScreen from '../AddFundsShared/FlowScreen';
import OptionRow from '../AddFundsShared/OptionRow';

type Props = {
    setActiveRightTab?: Function;
    currentAmount?: string;
    setPaymentProvider1Method?: Function;
    setPaymentProvider1Currency?: Function;
};

export default function AddFundsCardPaymentProvider1Select({
    setActiveRightTab,
    currentAmount,
    setPaymentProvider1Method,
    setPaymentProvider1Currency,
}: Props) {
    const styles = useStyles(stylesGenerator);
    const route = useRoute();
    const amount = currentAmount ? currentAmount : (route.params as any).amount;
    const { paymentMethods } = usePaymentProvider1Methods();
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const currency = useAppSelector(accountBalanceSelector)?.currency || '';

    useEffect(() => {
        if (!paymentMethods || !paymentMethods.currencies || paymentMethods.currencies.length === 0) {
            return;
        }
        const match = paymentMethods.currencies.find((c) => c === currency);
        setSelectedCurrency(match ?? paymentMethods.currencies[0]);
    }, [paymentMethods, currency]);

    const currencyOptions = useMemo(
        () => (paymentMethods?.currencies ?? []).map((c) => ({ value: c, label: c })),
        [paymentMethods]
    );

    function openPaymentCategory(cProviderId: string, cCurrency: string) {
        if (setPaymentProvider1Method) {
            setPaymentProvider1Method(cProviderId);
        }
        if (setPaymentProvider1Currency) {
            setPaymentProvider1Currency(cCurrency);
        }
        if (setActiveRightTab) {
            setActiveRightTab('add-money-card-payment-provider1');
        }
    }

    function RenderCards() {
        if (!paymentMethods.currencies || paymentMethods.currencies.length == 0) {
            return null;
        }
        const pm = paymentMethods.methodsPerCurrency[selectedCurrency];
        if (!pm || pm.length == 0) {
            return <Text style={styles.emptyText}>{'no-payment-methods'}</Text>;
        }
        return pm.map((paymentMethod: PaymentProvider1MethodItem, key) => (
            <RenderCardNormal paymentMethod={paymentMethod} key={key} />
        ));
    }

    function RenderCardNormal({ paymentMethod }) {
        const onPress = () => {
            isDesktop() && setActiveRightTab
                ? openPaymentCategory(paymentMethod.id, selectedCurrency)
                : linkTo(
                      paths.addFundsCardPaymentProvider1
                          .replace(':paymentProvider1Method', paymentMethod.id)
                          .replace(':currency', selectedCurrency)
                          .replace(':amount', amount)
                  );
        };
        return (
            <OptionRow
                label={paymentMethod.name}
                noTranslateLabel
                onPress={onPress}
                image={require('../../../../assets/files/mobileplatform/images/payments/visa_mastercard.gif')}
            />
        );
    }

    return (
        <FlowScreen
            title="select-currency-and-method"
            goToScreenName="add-money-category"
            setActiveRightTab={setActiveRightTab}
        >
            {paymentMethods ? (
                paymentMethods.currencies && paymentMethods.currencies.length > 0 ? (
                    <>
                        <FormField label="pay-currency">
                            <Select
                                value={selectedCurrency}
                                options={currencyOptions}
                                onChange={setSelectedCurrency}
                                placeholder="pay-currency"
                                searchable={currencyOptions.length > 8}
                            />
                        </FormField>
                        <RenderCards />
                    </>
                ) : (
                    <Text style={styles.emptyText}>{'no-payment-currency'}</Text>
                )
            ) : null}
        </FlowScreen>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        emptyText: {
            fontSize: FONT_SIZE_M,
            color: theme.textSecondary,
            textAlign: 'center',
            paddingVertical: scaleSize(24),
        },
    });
